import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Modal } from '@hologram-dimension/modal';
import useDevice from 'api/legacy/devices/useDevice';
import useRemovePhoneNumber from 'api/legacy/devices/useRemovePhoneNumber';
import useSimDetails from 'api/sims/useSimDetails';
import { useCallback, useState, type FC } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import { LegacyDevice } from 'types/legacyDashboard';
import styles from './RemovePhoneNumber.module.css';

interface RemovePhoneNumberProps {
  hologramSimId?: HologramSimId;
  deviceId?: DeviceId;
  onConfirmed: () => void;
  onCancelled: () => void;
}

// TODO: We will probably need more confirmation dialogs in the future, consider refactoring this into a generic confirmation dialog

const RemovePhoneNumber: FC<RemovePhoneNumberProps> = ({
  hologramSimId,
  deviceId,
  onConfirmed,
  onCancelled,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const organizationId = useOrganization();
  const { simDetails, mutate: mutateSimDetails } = useSimDetails(hologramSimId);
  const { device: legacyDeviceDetails, mutate: mutateDevice } = useDevice(deviceId, organizationId);
  const removePhoneNumber = useRemovePhoneNumber({ deviceId, organizationId });

  const onRemovePhoneNumber = useCallback(async () => {
    setError(null);
    setProcessing(true);

    try {
      const result = await removePhoneNumber();

      if (result?.success) {
        mutateSimDetails(
          { simDetails: { ...structuredClone(simDetails), phoneNumber: undefined } },
          { revalidate: false } // We WILL revalidate soon, but with DMS latency it might take a few seconds. Let this sit for now
        );
        mutateDevice({
          data: {
            ...structuredClone(legacyDeviceDetails as Required<LegacyDevice>),
            phonenumber: '',
            phonenumber_cost: '0.0000',
          },
          success: true,
        });
        onConfirmed();
      }
    } catch (ex) {
      setError(`${ex}`);
    }

    setProcessing(false);
  }, [
    legacyDeviceDetails,
    mutateDevice,
    mutateSimDetails,
    onConfirmed,
    removePhoneNumber,
    simDetails,
  ]);

  return (
    <Modal
      defaultOpen
      open={isOpen}
      setOpen={setIsOpen}
      onClosed={onCancelled}
      header="Remove phone number"
      footerActions={
        <>
          <Button variant="secondary" onClick={() => setIsOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onRemovePhoneNumber} loading={processing}>
            Remove phone number
          </Button>
        </>
      }
    >
      <div className={styles.contents}>
        If you remove the phone number for this SIM, the number you were using will be released and
        you will no longer be charged for the line. Please confirm that you no longer need it to
        communicate with your device.
        {error && <BannerNotification variant="system-error">{error}</BannerNotification>}
      </div>
    </Modal>
  );
};

export default RemovePhoneNumber;
